import { useState, useCallback } from 'react';

export interface NotificationData {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  message: string;
  autoClose?: boolean;
  duration?: number;
}

export const useNotification = () => {
  const [notifications, setNotifications] = useState<NotificationData[]>([]);

  const showNotification = useCallback((
    type: NotificationData['type'],
    message: string,
    options: {
      autoClose?: boolean;
      duration?: number;
    } = {}
  ) => {
    const id = Math.random().toString(36).substr(2, 9);
    const notification: NotificationData = {
      id,
      type,
      message,
      autoClose: options.autoClose ?? true,
      duration: options.duration ?? 5000
    };

    setNotifications(prev => [...prev, notification]);

    // Auto remove if autoClose is enabled
    if (notification.autoClose) {
      setTimeout(() => {
        removeNotification(id);
      }, notification.duration);
    }

    return id;
  }, []);

  const removeNotification = useCallback((id: string) => {
    setNotifications(prev => prev.filter(notification => notification.id !== id));
  }, []);

  const clearAllNotifications = useCallback(() => {
    setNotifications([]);
  }, []);

  // Convenient methods for different types
  const showSuccess = useCallback((message: string, options: { autoClose?: boolean; duration?: number } = {}) => {
    return showNotification('success', message, options);
  }, [showNotification]);

  const showError = useCallback((message: string, options: { autoClose?: boolean; duration?: number } = {}) => {
    return showNotification('error', message, { ...options, duration: options.duration ?? 7000 });
  }, [showNotification]);

  const showWarning = useCallback((message: string, options: { autoClose?: boolean; duration?: number } = {}) => {
    return showNotification('warning', message, options);
  }, [showNotification]);

  const showInfo = useCallback((message: string, options: { autoClose?: boolean; duration?: number } = {}) => {
    return showNotification('info', message, options);
  }, [showNotification]);

  return {
    notifications,
    showNotification,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    removeNotification,
    clearAllNotifications
  };
};